import { useEffect, useEffectEvent, useRef, useState } from "react";
import { SUPERSET_LOCALE_URL_PARAM, useGetEmbeddedSupersetData } from "src/api/superset.ts";
import { resolveRequestLocale } from "src/api/axiosClient/userApi";
import { Loading } from "src/components/utils/Loading.tsx";
import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { watchChartsDataEmptiness } from "src/components/Superset/watchChartsDataEmptiness.ts";

type SupersetChartEmbedProps = {
    src: string;
    height?: number;
    hideWhenEmpty?: boolean;
    onEmptyChange?: (isEmpty: boolean) => void;
};

const DEFAULT_CHART_HEIGHT = 400;

const buildChartUrl = (src: string, urlParams: Record<string, unknown>, locale: string): URL | null => {
    try {
        const url = new URL(src);

        Object.entries(urlParams).forEach(([key, value]) => {
            url.searchParams.set(key, Array.isArray(value) ? value.join(",") : String(value));
        });
        url.searchParams.set(SUPERSET_LOCALE_URL_PARAM, locale);

        return url;
    } catch {
        return null;
    }
};

export const SupersetChartEmbed = (props: SupersetChartEmbedProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { src, height = DEFAULT_CHART_HEIGHT, hideWhenEmpty = false, onEmptyChange } = props;
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [isAwaitingEmptiness, setIsAwaitingEmptiness] = useState(hideWhenEmpty);
    const {
        data: embeddedData,
        isLoading,
        isError,
    } = useGetEmbeddedSupersetData({
        resources: [],
    });

    const locale = resolveRequestLocale(undefined, i18n.resolvedLanguage);
    const chartUrl = embeddedData ? buildChartUrl(src, embeddedData.urlParams, locale) : null;
    const chartUrlString = chartUrl?.toString() ?? null;

    const reportEmpty = useEffectEvent(() => {
        onEmptyChange?.(true);
    });

    useEffect(() => {
        if (!hideWhenEmpty || chartUrlString === null) {
            setIsAwaitingEmptiness(false);
            return;
        }

        setIsAwaitingEmptiness(true);

        return watchChartsDataEmptiness({
            origin: new URL(chartUrlString).origin,
            getWindow: () => iframeRef.current?.contentWindow ?? null,
            onDecision: (isEmpty) => {
                if (isEmpty === true) {
                    reportEmpty();
                    return;
                }

                setIsAwaitingEmptiness(false);
            },
        });
    }, [chartUrlString, hideWhenEmpty]);

    if (isLoading || !embeddedData) {
        return <Loading />;
    }

    if (isError || chartUrlString === null) {
        return (
            <EmptyStateCard
                title={t("common.error_occurred", "An error occurred")}
                description={t("superset.component.chart.invalid_url")}
            />
        );
    }

    return (
        <iframe
            ref={iframeRef}
            style={{
                borderRadius: theme.spacing(2),
                visibility: isAwaitingEmptiness ? "hidden" : "visible",
            }}
            title={"superset-chart"}
            frameBorder="0"
            scrolling="no"
            seamless
            width="100%"
            height={`${height}px`}
            src={chartUrlString}
        />
    );
};

export default SupersetChartEmbed;
